import React, {useRef, useState, useEffect, useCallback, useMemo} from 'react'
import {createPortal} from 'react-dom'

import {
  Wrapper,
  FieldRow,
  InputWrapper,
  StyledInput,
  ChevronButton,
  ActionButton,
  ActionButtonIcon,
  ActionButtonLabel,
  DropdownList,
  DropdownItem,
  DropdownEmpty,
  DropdownLoading,
  HelperText,
  LabelRow,
  LabelEl,
  LabelReq,
  ClearButton,
  SpinnerIcon,
} from './SearchInput.style'

// Matches the max-height in SearchInput.style.js; min keeps a few rows visible
const DROPDOWN_MAX_HEIGHT = 220
const DROPDOWN_MIN_HEIGHT = 120

// ─────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────

function ChevronIcon({open}) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 200ms ease',
      }}
    >
      <path
        d="M3 5L7 9L11 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ClearIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 2L10 10M10 2L2 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

// ─────────────────────────────────────────────
// Debounce hook
// ─────────────────────────────────────────────

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

// ─── React 17-compatible unique id ───────────────────────────────────────────
// useId() is React 18+ only. This module-level counter is stable across
// renders and never collides, unlike a randomly sampled suffix. These ids only
// wire up aria-* relationships, so they need uniqueness, not unpredictability.
let idCounter = 0

// ─────────────────────────────────────────────
// SearchInput Component
// ─────────────────────────────────────────────

/**
 * SearchInput
 *
 * A debounced search field with async API support, an optional dropdown list,
 * and a configurable action button — styled to match the existing Input design system.
 *
 * ── INPUT ──────────────────────────────────────────────────────────────────
 * @param {string}        label              Visible label above the field
 * @param {boolean}       required           Shows a red * next to the label
 * @param {string}        placeholder        Input placeholder text (default: 'Search…')
 * @param {string}        value              Controlled value
 * @param {Function}      onChange           Called with raw input value on every keystroke
 * @param {Function}      onDebouncedChange  Called with debounced value after `debounceDelay` ms
 * @param {number}        debounceDelay      Debounce delay in ms (default: 300)
 *
 * ── ASYNC / DROPDOWN ───────────────────────────────────────────────────────
 * @param {Function}      fetchOptions       Async fn: (query: string) => Promise<Option[]>
 *                                           Use this for API-driven dropdowns. When provided,
 *                                           the component manages loading/error state internally.
 *                                           Signature: async (query) => [{ label, value, icon? }]
 *
 * @param {Array}         options            Static dropdown options [{ label, value, icon? }]
 *                                           Use this for a pre-known list. Ignored when fetchOptions is set.
 *
 * @param {boolean}       loading            External loading override (shows spinner in dropdown)
 * @param {string}        fetchError         External error message to show in dropdown
 * @param {Function}      onSelect           Called with the chosen option object
 * @param {string}        selectedValue      Currently highlighted dropdown value (controlled)
 * @param {boolean}       clearable          Show a × button when there's a value
 * @param {Function}      onClear            Called when × is clicked
 * @param {string}        emptyMessage       Message when options list is empty (default: 'No results found.')
 * @param {string}        loadingMessage     Message shown while loading (default: 'Loading…')
 * @param {Function}      filterFn           Custom filter for static options: (option, query) => boolean
 * @param {number}        minCharsToFetch    Min chars before fetchOptions is called (default: 1)
 *
 * ── ACTION BUTTON ──────────────────────────────────────────────────────────
 * @param {ReactNode}     actionIcon         Icon inside the action button
 * @param {string}        actionLabel        Button text (default: 'Apply')
 * @param {Function}      onAction           Called with current input value + selected option
 * @param {boolean}       actionDisabled     Disables the action button
 * @param {string}        actionVariant      'primary' | 'secondary' | 'ghost' (default: 'primary')
 * @param {boolean}       hideAction         Hides the action button entirely
 *
 * ── MISC ───────────────────────────────────────────────────────────────────
 * @param {string}        variant            'default' | 'form' (default: 'default')
 * @param {string}        size               'sm' | 'md' | 'lg' (default: 'md')
 * @param {boolean}       disabled           Disables the whole input
 * @param {string}        errorText          Error message shown below the field
 * @param {string}        helperText         Helper text shown below (when no error)
 * @param {string}        className          Extra class on the outer wrapper
 * @param {object}        style              Extra inline style on the outer wrapper
 * @param {string}        id                 Override the auto-generated input id
 */
export function SearchInput({
  // Label
  label,
  required = false,

  // Input
  placeholder = 'Search…',
  value: externalValue,
  onChange,
  onDebouncedChange,
  debounceDelay = 300,

  // Async
  fetchOptions,
  loading: externalLoading = false,
  fetchError: externalFetchError,
  minCharsToFetch = 1,
  loadingMessage = 'Loading…',

  // Dropdown
  options,
  onSelect,
  selectedValue,
  clearable = false,
  onClear,
  emptyMessage = 'No results found.',
  filterFn,

  // Action button
  actionIcon,
  actionLabel = 'Apply',
  onAction,
  actionDisabled = false,
  actionVariant = 'primary',
  hideAction = false,

  // Misc
  variant = 'default',
  size = 'md',
  disabled = false,
  errorText,
  helperText,
  className,
  style,
  id: externalId,

  ...nativeProps
}) {
  // ── IDs ───────────────────────────────────────────────────────────────────
  const idRef = useRef(null)
  if (!idRef.current)
    idRef.current = externalId ?? `search-input-${++idCounter}`
  const inputId = idRef.current
  const errorId = `${inputId}-error`
  const helperId = `${inputId}-helper`
  const listboxId = `${inputId}-listbox`

  // ── Internal state ────────────────────────────────────────────────────────
  const [internalValue, setInternalValue] = useState('')
  const [open, setOpen] = useState(false)
  const [asyncOptions, setAsyncOptions] = useState([])
  const [internalLoading, setInternalLoading] = useState(false)
  const [internalFetchError, setInternalFetchError] = useState(null)
  const [selectedOption, setSelectedOption] = useState(null)

  const wrapperRef = useRef(null)
  const dropdownRef = useRef(null)
  // Tracks the latest fetch so stale responses from slow calls are discarded
  const fetchIdRef = useRef(0)
  // handleSelect writes the chosen option's display label into the input
  // (see below) so the field shows human-readable text instead of the raw
  // code/value. That label change flows through the same debounced-value
  // effect that drives search-as-you-type, which would otherwise immediately
  // re-search using the label itself right after every selection — wasteful,
  // and broken outright whenever the label contains characters a raw URL path
  // segment can't hold unescaped (e.g. a coupon named "50% Off ..."). Skip
  // exactly one fetch when the debounced value is the label we just set.
  const skipNextFetchValueRef = useRef(null)
  const [dropdownPos, setDropdownPos] = useState({top: 0, left: 0, width: 0})

  const isControlled = externalValue !== undefined
  const inputValue = isControlled ? externalValue : internalValue

  const debouncedValue = useDebounce(inputValue, debounceDelay)

  const isAsync = Boolean(fetchOptions)
  const isLoading = isAsync ? internalLoading : externalLoading
  const resolvedFetchError = isAsync ? internalFetchError : externalFetchError
  const hasDropdown = isAsync || Boolean(options)

  // ── Async fetch on debounced change ───────────────────────────────────────
  useEffect(() => {
    if (!isAsync) {
      onDebouncedChange?.(debouncedValue)
      return
    }

    if (debouncedValue.length < minCharsToFetch) {
      setAsyncOptions([])
      setInternalFetchError(null)
      return
    }

    if (
      skipNextFetchValueRef.current !== null &&
      debouncedValue === skipNextFetchValueRef.current
    ) {
      skipNextFetchValueRef.current = null
      return
    }

    const fetchId = ++fetchIdRef.current

    const run = async () => {
      setInternalLoading(true)
      setInternalFetchError(null)
      try {
        const results = await fetchOptions(debouncedValue)
        if (fetchId !== fetchIdRef.current) return // stale — discard
        setAsyncOptions(results ?? [])
        if (results?.length) setOpen(true)
      } catch (err) {
        if (fetchId !== fetchIdRef.current) return
        setInternalFetchError(err?.message ?? 'Failed to load options.')
        setAsyncOptions([])
      } finally {
        if (fetchId === fetchIdRef.current) setInternalLoading(false)
      }
    }

    run()
    onDebouncedChange?.(debouncedValue)
  }, [debouncedValue]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filtered options (static mode) ────────────────────────────────────────
  const filteredStaticOptions = useMemo(() => {
    if (!options) return []
    if (!inputValue) return options
    const defaultFilter = (opt, q) =>
      opt.label.toLowerCase().includes(q.toLowerCase())
    const fn = filterFn ?? defaultFilter
    return options.filter(opt => fn(opt, inputValue))
  }, [options, inputValue, filterFn])

  const displayedOptions = isAsync ? asyncOptions : filteredStaticOptions

  // ── Close on outside click ────────────────────────────────────────────────
  // Must check both the field wrapper AND the portaled dropdown — the portal
  // lives in document.body so it is NOT inside wrapperRef.
  useEffect(() => {
    if (!open) return
    const handler = e => {
      const inField = wrapperRef.current?.contains(e.target)
      const inDropdown = dropdownRef.current?.contains(e.target)
      if (!inField && !inDropdown) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // ── Portal dropdown positioning ───────────────────────────────────────────
  // Measures the field's screen rect so the portaled dropdown can use
  // position:fixed and escape any overflow:hidden/auto ancestor.
  useEffect(() => {
    if (!open || !wrapperRef.current) return
    const measure = () => {
      if (!wrapperRef.current) return
      const rect = wrapperRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom - 8
      const spaceAbove = rect.top - 8
      // Flip upward only when the list would be clipped below and there is more room above
      const openUp = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow
      const available = openUp ? spaceAbove : spaceBelow
      setDropdownPos({
        // 'auto' (not undefined) so it overrides DropdownList's CSS top: calc(100% + 4px)
        top: openUp ? 'auto' : rect.bottom + 4,
        bottom: openUp ? window.innerHeight - rect.top + 4 : 'auto',
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(
          DROPDOWN_MIN_HEIGHT,
          Math.min(DROPDOWN_MAX_HEIGHT, available),
        ),
      })
    }
    measure()
    // capture:true catches scroll on any ancestor (e.g. an overflow:auto panel)
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [open])

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = useCallback(
    e => {
      const val = e.target.value
      if (!isControlled) setInternalValue(val)
      onChange?.(val)
      // Clear selected option if user edits the field
      setSelectedOption(null)
      // For static mode: open dropdown immediately as user types
      if (!isAsync && options && val) setOpen(true)
    },
    [isControlled, onChange, isAsync, options],
  )

  const handleSelect = useCallback(
    opt => {
      skipNextFetchValueRef.current = opt.label
      if (!isControlled) setInternalValue(opt.label)
      onChange?.(opt.label)
      setSelectedOption(opt)
      onSelect?.(opt)
      setOpen(false)
    },
    [isControlled, onChange, onSelect],
  )

  const handleClear = useCallback(() => {
    if (!isControlled) setInternalValue('')
    onChange?.('')
    setSelectedOption(null)
    setAsyncOptions([])
    setInternalFetchError(null)
    onClear?.()
    setOpen(false)
    ++fetchIdRef.current // cancel any in-flight fetch
  }, [isControlled, onChange, onClear])

  const handleChevron = useCallback(() => {
    setOpen(prev => !prev)
  }, [])

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') setOpen(false)
      if (e.key === 'Enter' && !open) onAction?.(inputValue, selectedOption)
    },
    [onAction, inputValue, selectedOption, open],
  )

  // ── Derived ───────────────────────────────────────────────────────────────
  const hasError = Boolean(errorText)
  const showClear = clearable && Boolean(inputValue)
  const showSpinner = isLoading

  const describedBy = [errorText ? errorId : null, helperText ? helperId : null]
    .filter(Boolean)
    .join(' ')

  // Dropdown should be open when: explicitly open AND (loading, error, or has options)
  const dropdownVisible =
    open &&
    (isLoading ||
      resolvedFetchError ||
      displayedOptions.length > 0 ||
      // show "no results" state only after a fetch attempt
      (isAsync && debouncedValue.length >= minCharsToFetch && !isLoading))

  return (
    <Wrapper ref={wrapperRef} className={className} style={style}>
      {/* Label */}
      {label && (
        <LabelRow>
          <LabelEl htmlFor={inputId} variant={variant}>
            {label}
          </LabelEl>
          {required && <LabelReq>*</LabelReq>}
        </LabelRow>
      )}

      {/* Field row: input + action button */}
      <FieldRow
        className="search-field-row"
        hasError={hasError}
        variant={variant}
        disabled={disabled}
      >
        <InputWrapper>
          <StyledInput
            id={inputId}
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-haspopup={hasDropdown ? 'listbox' : undefined}
            aria-invalid={hasError}
            aria-describedby={describedBy || undefined}
            placeholder={placeholder}
            value={inputValue}
            onChange={handleChange}
            onFocus={() => {
              if (!isAsync && options && filteredStaticOptions.length)
                setOpen(true)
              if (isAsync && asyncOptions.length) setOpen(true)
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            size={size}
            {...nativeProps}
          />

          {/* Spinner — shown while fetching */}
          {showSpinner && <SpinnerIcon aria-label="Loading" />}

          {/* Clear button */}
          {!showSpinner && showClear && (
            <ClearButton
              type="button"
              onClick={handleClear}
              aria-label="Clear"
              tabIndex={0}
            >
              <ClearIcon />
            </ClearButton>
          )}

          {/* Chevron toggle */}
          {hasDropdown && !showSpinner && (
            <ChevronButton
              type="button"
              onClick={handleChevron}
              aria-label={open ? 'Close dropdown' : 'Open dropdown'}
              tabIndex={0}
              disabled={disabled}
            >
              <ChevronIcon open={open} />
            </ChevronButton>
          )}
        </InputWrapper>

        {/* Divider + Action button */}
        {!hideAction && (
          <ActionButton
            type="button"
            actionVariant={actionVariant}
            onClick={() => onAction?.(inputValue, selectedOption)}
            disabled={actionDisabled || disabled || isLoading}
            aria-label={actionLabel}
          >
            {actionIcon && <ActionButtonIcon>{actionIcon}</ActionButtonIcon>}
            {actionLabel && (
              <ActionButtonLabel>{actionLabel}</ActionButtonLabel>
            )}
          </ActionButton>
        )}
      </FieldRow>

      {/* Dropdown — rendered via portal so it escapes overflow:hidden/auto parents */}
      {hasDropdown &&
        dropdownVisible &&
        createPortal(
          <DropdownList
            ref={dropdownRef}
            id={listboxId}
            role="listbox"
            aria-label="Options"
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              bottom: dropdownPos.bottom,
              left: dropdownPos.left,
              width: dropdownPos.width,
              maxHeight: dropdownPos.maxHeight,
            }}
          >
            {/* Loading state */}
            {isLoading && (
              <DropdownLoading role="status" aria-live="polite">
                {loadingMessage}
              </DropdownLoading>
            )}

            {/* Fetch error */}
            {!isLoading && resolvedFetchError && (
              <DropdownEmpty isError>{resolvedFetchError}</DropdownEmpty>
            )}

            {/* Empty results */}
            {!isLoading &&
              !resolvedFetchError &&
              displayedOptions.length === 0 && (
                <DropdownEmpty>{emptyMessage}</DropdownEmpty>
              )}

            {/* Options */}
            {!isLoading &&
              !resolvedFetchError &&
              displayedOptions.map(opt => (
                <DropdownItem
                  key={opt.value}
                  role="option"
                  aria-selected={
                    opt.value === (selectedOption?.value ?? selectedValue)
                  }
                  isSelected={
                    opt.value === (selectedOption?.value ?? selectedValue)
                  }
                  onClick={() => handleSelect(opt)}
                  onKeyDown={e => e.key === 'Enter' && handleSelect(opt)}
                  tabIndex={0}
                >
                  {opt.icon && (
                    <span style={{display: 'flex', alignItems: 'center'}}>
                      {opt.icon}
                    </span>
                  )}
                  {opt.label}
                </DropdownItem>
              ))}
          </DropdownList>,
          document.body,
        )}

      {/* Helper / Error */}
      {hasError ? (
        <HelperText id={errorId} isError role="alert">
          {errorText}
        </HelperText>
      ) : (
        helperText && <HelperText id={helperId}>{helperText}</HelperText>
      )}
    </Wrapper>
  )
}
